// TODO: Separate into multiple contexts, keeping simple for now

"use client"

import { ChatbotUIContext } from "@/context/context"
import { getSortedData } from "@/components/utility/list-sorter"
import { getAllProfiles, getProfileByUserId, getTenancyProfiles } from "@/db/profiles"
import { getWorkspaceImageFromStorage } from "@/db/storage/workspace-images"
import { getUserWorkspacesByTenantId, isTenantShared } from "@/db/workspaces"
import { convertBlobToBase64 } from "@/lib/blob-to-b64"
import {
  fetchHostedModels,
  fetchOllamaModels,
  fetchOpenRouterModels
} from "@/lib/models/fetch-models"
import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import {
	ChatMessage,
  ChatSettings,
  LLM,
  LLMID,
  ModelProvider,
  OpenRouterLLM,
  TenantUser
} from "@/types/base"
import {
	validateAssistants as libValidateAssistants,
	validateChats as libValidateChats,
	validateCollections as libValidateCollections,
	validateFiles as libValidateFiles,
	validateGroups as libValidateGroups,
	validateModels as libValidateModels,
	validatePresets as libValidatePresets,
	validateProfiles as libValidateProfiles,
	validatePrompts as libValidatePrompts,
	validateProviders as libValidateProviders,
	validateRoles as libValidateRoles,
	validateTenants as libValidateTenants,
	validateMcps as libValidateMcps,
	validateTools as libValidateTools
} from '@/db/validate'

import { WorkspaceImage } from "@/types/images/workspace-image"
import { getAllProfileUsers } from "@/db/tenant-users"
import { AssistantImage } from "@/types/images/assistant-image"
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { getAllTenants, getTenantById, isAidonAdmin, isTenantAdmin } from "@/db/tenants"
import { getAllRoles } from "@/db/roles"
import { getTenantGroupsByTenantId } from "@/db/groups"
import Authuser, { UserData, Groups } from "@/types/base/authuser"
import { SettingsState } from "@/lib/settings/settings-state"
import { loadSettings } from "@/lib/settings/settings-utils"
import { SessionRealtimePresenceState, UserSession } from "@/config/config"
import { FolderWithElements } from "@/types/base/folder_with_elements"
import { PermAction } from "@/types/base/permissions"
import { getAllProviders } from "@/db/providers"
import { ModelRecordWithProviderName } from "@/db/models"
import { getImageSize } from "@/lib/pricing_utils"
import { keyMap, profileKeys } from "@/types/base/valid-keys"
import { logError } from "@/utils/errorLogging"
import { blobToUint8Array } from "@/utils/type_utils"
import { getClientSubscriptionManager } from "@/lib/realtime/client-subscription-manager"
import { makePresetChangeHandler, makePresetWorkspaceHandler } from "@/lib/realtime/client-handlers/presets-handler"
import { HandlerConfig } from "@/lib/realtime/base-subscription-manager"
import { makeAssistantChangeHandler, makeAssistantWorkspaceHandler } from "@/lib/realtime/client-handlers/assistants-handler"
import { makeCollectionChangeHandler, makeCollectionWorkspaceHandler } from "@/lib/realtime/client-handlers/collections-handler"
import { makeFileChangeHandler, makeFileWorkspaceHandler } from "@/lib/realtime/client-handlers/files-handler"
import { makeFolderChangeHandler, makeFolderElementsHandler } from "@/lib/realtime/client-handlers/folders-handler"
import { makeGroupChangeHandler, makeGroupWorkspaceHandler } from "@/lib/realtime/client-handlers/groups-handler"
import { makeModelChangeHandler, makeModelWorkspaceHandler } from "@/lib/realtime/client-handlers/models-handler"
import { makePromptChangeHandler, makePromptWorkspaceHandler } from "@/lib/realtime/client-handlers/prompts-handler"
import { makeToolChangeHandler, makeToolWorkspaceHandler } from "@/lib/realtime/client-handlers/tools-handler"
import { createClientLogger } from "@/utils/logger/clientLogger"
import { makeProviderHandler } from "@/lib/realtime/client-handlers/providers-handler"
import { makeWorkspaceHandler } from "@/lib/realtime/client-handlers/workspaces-handler"
import { makeChatHandler } from "@/lib/realtime/client-handlers/chats-handler"
import { printObject } from "@/lib/utils"
import { makeMcpChangeHandler, makeMcpWorkspaceHandler } from "@/lib/realtime/client-handlers/mcps-handler"

const logger = createClientLogger('utility.globalState', supabase)

interface GlobalStateProps {
  children: React.ReactNode
}

export const GlobalState: FC<GlobalStateProps> = ({ children }) => {
  // const router = useRouter()

  const [isInitialized, setInitialized] = useState<boolean>(false)

  // PROFILE STORE
  const [profile, setProfile] = useState<Tables<"profiles"> | null>(null)

  // USER SETTINGS
  const [settings, setSettings] = useState<SettingsState | null>(null)

	// USER SESSION
	const [userSession, setUserSession] = useState<UserSession | null>(null)

	// USER SESSIONS
	const [sessions, setSessions] = useState<SessionRealtimePresenceState | null>(null)

	// AUTH USER
  const [authuser, setAuthuser] = useState<Authuser | null>(null)

  // USERS STORE
  const [users, setUsers] = useState<TenantUser[]>([])

  // ITEMS STORE
  const [assistants, setAssistants] = useState<Tables<"assistants">[]>([])
  const [collections, setCollections] = useState<Tables<"collections">[]>([])
	const [chatsMap, setChatsMap] = useState<Map<string, Tables<"chats">>>(new Map())
	const [chatIds, setChatIds] = useState<string[]>([])
  const [files, setFiles] = useState<Tables<"files">[]>([])
  const [folders, setFolders] = useState<FolderWithElements[]>([])
  const [mcps, setMcps] = useState<Tables<"mcps">[]>([])
  const [models, setModels] = useState<ModelRecordWithProviderName[]>([])
  const [presets, setPresets] = useState<Tables<"presets">[]>([])
  const [profiles, setProfiles] = useState<Tables<"profiles">[]>([])
  const [prompts, setPrompts] = useState<Tables<"prompts">[]>([])
  const [providers, setProviders] = useState<Tables<"providers">[]>([])
  const [tenants, setTenants] = useState<Tables<"tenants">[]>([])
  const [roles, setRoles] = useState<Tables<"roles">[]>([])
  const [groups, setGroups] = useState<Tables<"groups">[]>([])
  const [tools, setTools] = useState<Tables<"tools">[]>([])
  const [workspaces, setWorkspaces] = useState<Tables<"workspaces">[]>([])

  // MODELS STORE
  const [envKeyMap, setEnvKeyMap] = useState<Record<string, boolean>>({})
  const [tenantKeyMap, setTenantKeyMap] = useState<Record<string, boolean>>({})
  const [tenantBillableKeyMap, setTenantBillableKeyMap] = useState<Record<string, boolean>>({})
  const [tenantSharedKeyMap, setTenantSharedKeyMap] = useState<Record<string, boolean>>({})
  const [availableHostedModels, setAvailableHostedModels] = useState<LLM[]>([])
  const [availableLocalModels, setAvailableLocalModels] = useState<LLM[]>([])
  const [availableOpenRouterModels, setAvailableOpenRouterModels] = useState<OpenRouterLLM[]>([])

  // WORKSPACE STORE
  const [selectedWorkspace, setSelectedWorkspace] = useState<Tables<"workspaces"> | null>(null)
  const [workspaceImages, setWorkspaceImages] = useState<WorkspaceImage[]>([])

  // PRESET STORE
  const [selectedPreset, setSelectedPreset] = useState<Tables<"presets"> | null>(null)

  // ASSISTANT STORE
  const [selectedAssistant, setSelectedAssistant] = useState<Tables<"assistants"> | null>(null)
  const [assistantImages, setAssistantImages] = useState<AssistantImage[]>([])
  const [openaiAssistants, setOpenaiAssistants] = useState<any[]>([])

  // PASSIVE CHAT STORE
  const [chatSettings, setChatSettings] = useState<ChatSettings>({
    model: "gpt-4.1-nano",
    prompt: "You are a helpful AI assistant.",
    temperature: 0.5,
    contextLength: 4000,
		maxOutputToken: 4096,
		supportsImages: true,
		supportsReasoning: false,
		supportsTools: true,
    includeProfileContext: true,
    includeWorkspaceInstructions: true,
    embeddingsProvider: "openai"
  })

	const [selectedChat, setSelectedChat] = useState<Tables<"chats"> | null>(null)
  const [showDashboard, setShowDashboard] = useState<boolean>(false)


	// REFS
	//Those next 2 never change during the life of a user session so not managed with useEffect
	const authuserRef = useRef<Authuser | null>(null)
	const tenantIdRef = useRef<string | null>(null)

	const workspaceId = selectedWorkspace?.id ?? null
	const workspaceIdRef = useRef<string | null>(workspaceId)
	useEffect(() => {
		workspaceIdRef.current = workspaceId
	}, [workspaceId])

	const selectedChatId = selectedChat?.id ?? null
	const selectedChatIdRef = useRef<string | null>(selectedChatId)
	useEffect(() => {
		selectedChatIdRef.current = selectedChatId
	}, [selectedChatId])

	const chatMessagesRef = useRef<ChatMessage[]>([])
	const setChatMessagesRef = useRef<Dispatch<SetStateAction<ChatMessage[]>>>(() => {})
	const setChatFileItemsRef = useRef<Dispatch<SetStateAction<string[]>>>(() => {})


	//GENERAL STATUS
	// const [sidebarIsOpen, setSidebarOpen] = useState<boolean>(false)

	const allModels = useMemo(() => [
		...models.map(model => ({
      modelId: model.model_id as LLMID,
      modelName: model.name,
			active: !(model.removed || model.deprecated),
      provider: model.provider_name as ModelProvider,
      hostedId: model.id,
      platformLink: "",
      imageInput: model.supports_images,
      toolInput: model.supports_tools,
			reasoning: model.supports_reasoning,
			maxContext: model.context_length,
			maxOutputToken: model.token_output_length,
			minTemperature: model.min_temperature,
			maxTemperature: model.max_temperature,
			pricing: {
				currency: model.pricing_currency,
				unit: model.pricing_unit,
				inputCost: model.pricing_input,
				outputCost: model.pricing_output,
				image: model.pricing_image,
				request: model.pricing_request,
				cacheRead: model.pricing_cache_read,
				cacheWrite: model.pricing_cache_write,
				reasoning: model.pricing_reasoning,
				webSearch: model.pricing_web_search,
			}
    })),
		...availableLocalModels,
		...availableOpenRouterModels
	], [models, availableLocalModels, availableOpenRouterModels])

	const chats = useMemo(
		() => chatIds.map(id => chatsMap.get(id)).filter(Boolean) as Tables<"chats">[],
		[chatIds, chatsMap]
	)

	const setChats = useCallback((updater: SetStateAction<Tables<"chats">[]>) => {
		setChatsMap(prevMap => {
			const prevArray = Array.from(prevMap.values())
			const nextArray: Tables<'chats'>[] =
				typeof updater === 'function'
					? (updater as (_: Tables<'chats'>[]) => Tables<'chats'>[])(prevArray)
					: updater
			const nextMap = new Map(nextArray.map(chat => [chat.id, chat]))
			setChatIds(nextArray.map(chat => chat.id))
			return nextMap
		})
	}, [] ) //No dependencies, do not close over chatIds

	const getTenantId = (): string | null => {
		return tenantIdRef.current
	}

  const validateAssistants = (assistant: Tables<"assistants">, _action: PermAction):string | null => {
		return libValidateAssistants(assistant, _action)
	}

	const validateChats = (_chat: Tables<"chats">, _action: PermAction):string | null => {
		return libValidateChats(_chat, _action)
	}

  const validateCollections = (collection: Tables<"collections">, _action: PermAction):string | null => {
		return libValidateCollections(collection, _action)
	}

	const validateFiles = (file: Tables<"files">, action: PermAction):string | null => {
		return libValidateFiles(file, action)
	}

  const validateGroups = (group: Tables<"groups">, _action: PermAction):string | null => {
		return libValidateGroups(group, _action)
	}

  const validateModels = (model: Tables<"models">, _action: PermAction):string | null => {
		return libValidateModels(model, _action)
	}

  const validatePresets = (preset: Tables<"presets">, _action: PermAction):string | null => {
		return libValidatePresets(preset, _action)
	}

	const validateProfiles = (profile: Tables<"profiles">, _action: PermAction):string | null => {
		return libValidateProfiles(profile, _action)
	}

	const validatePrompts = (prompt: Tables<"prompts">, _action: PermAction):string | null => {
		return libValidatePrompts(prompt, _action)
	}

	const validateProviders = (provider: Tables<"providers">, _action: PermAction):string | null => {
		return libValidateProviders(provider, _action)
	}

  const validateRoles = (_role: Tables<"roles">, _action: PermAction):string | null => {
		return libValidateRoles(_role, _action)
	}

  const validateTenants = (tenant: Tables<"tenants">, _action: PermAction):string | null => {
		return libValidateTenants(tenant, _action)
	}

  const validateMcps = (mcp: Tables<"mcps">, _action: PermAction):string | null => {
		return libValidateMcps(mcp, _action)
	}

  const validateTools = (tool: Tables<"tools">, _action: PermAction):string | null => {
		return libValidateTools(tool, _action)
	}

	const isSharedConfig = async (elementSharing: string, owningTenant: string): Promise<boolean>  => {
		if (authuser) {
			const aidonTenantId = await authuser.getAidonTenantId(supabase)
			const tenantId = await authuser.getTenantId(supabase)

			//in Aidon it is shared if you are on a private workspace (i.e. an Aidon user)
			if (tenantId == aidonTenantId) {
				return selectedWorkspace?.sharing == 'private'
			}
			else {  //for all other tenants
				if (owningTenant != tenantId) {  //if you don't own it, it was shared (almost implies that owningTenant is Aidon)
					return true
				}

				const sharedWorkspace = isTenantShared(selectedWorkspace)  //this is a tenancy workspace and not a user WS
				if (sharedWorkspace) {
					return false
				}

				const isPublic = elementSharing != 'private'
				return isPublic  //now in your own private workspace and public then it was shared
			}
		}
		return false
	}

	const isProviderKeyActive = (provider: string): boolean => {
		const envKey = keyMap[provider]
		const profileKey = profileKeys[envKey as keyof typeof profileKeys]
		const providerKey: keyof typeof profile = `${profileKey}` as keyof typeof profile

		const envKeyActive = envKeyMap[provider]
		const tenantKeyActive = tenantKeyMap[provider]
		const tenantBillableKeyActive = tenantBillableKeyMap[provider]
		const tenantSharedKeyActive = tenantSharedKeyMap[provider]

		return profile?.[providerKey] || !!envKeyActive || !!tenantKeyActive || !!tenantBillableKeyActive ||
				!!tenantSharedKeyActive
	}

	/* eslint-disable react-hooks/exhaustive-deps */
  const fetchStartingData = useCallback(async () => {
    const session = (await supabase.auth.getSession()).data.session

    if (session) {
      const user = session.user

      const groups: Groups = user.app_metadata.groups
      const permissions: string[] = []

      const profile = await getProfileByUserId(supabase, user.id)
      if (profile) {
        setProfile(profile)

        const userData: UserData = {
          id: user.id,
          name: profile.display_name,
          email: profile.username
        }

        const tmpAuthuser = new Authuser(userData, groups, permissions)
        setAuthuser(tmpAuthuser)

				const userSettings = loadSettings()
				setSettings(userSettings)

        const providerData = await getAllProviders(supabase)
        setProviders(getSortedData(userSettings, "providers", providerData))

        const roleData = await getAllRoles(supabase)
        setRoles(getSortedData(userSettings, "roles", roleData))

				let tenantId: string | undefined

        if (tmpAuthuser) {
          // console.log(tmpAuthuser)
					authuserRef.current = tmpAuthuser
					tenantId = await tmpAuthuser.getTenantId(supabase)
					tenantIdRef.current = tenantId
          const groupData = await getTenantGroupsByTenantId(supabase, tenantId)
          setGroups(getSortedData(userSettings, "groups", groupData))

					const aidonAdmin = await isAidonAdmin(supabase, tmpAuthuser)
          const workspaces = await getUserWorkspacesByTenantId(supabase, tenantId, aidonAdmin)
          setWorkspaces(getSortedData(userSettings, "workspaces", workspaces))

					if (aidonAdmin) {  //access to all profiles
						const profileData = await getAllProfiles(supabase)
						setProfiles(profileData)

						const tenantData = await getAllTenants(supabase)
						setTenants(tenantData)
					}
					else if (tenantId) {
						const tenantData = await getTenantById(supabase, tenantId)
						const tenantAdmin = await isTenantAdmin(tmpAuthuser)
						if (tenantAdmin) {  //has access to all tenancy profiles (== own only on single user tenancy)
							const profileData = await getTenancyProfiles(supabase, tenantData.id)
							setProfiles(profileData)
						}
						// else {  //any use has access to their own profile
						// 	setProfiles([profile])
						// }
						setTenants([tenantData])
					}
				}

        // if (!profile?.has_onboarded) {
        //   return router.push("/welcome")
        // }

        for (const workspace of workspaces) {
          let workspaceImageUrl = ""

          if (workspace.image_path) {
            workspaceImageUrl = (await getWorkspaceImageFromStorage(workspace.image_path)) || ""
          }

          if (workspaceImageUrl) {
            const response = await fetch(workspaceImageUrl)
            const blob = await response.blob()
						const size = getImageSize(await blobToUint8Array(blob))
            const base64 = await convertBlobToBase64(blob)

            setWorkspaceImages(prev => [
              ...prev,
              {
                workspaceId: workspace.id,
                path: workspace.image_path,
								size,
                base64: base64,
                url: workspaceImageUrl
              }
            ])
          }
        }

        return {profile, tenantId}
      }
    }
		else {
			logger.warn(`No session found to run fetchStartingData`)
		}
  }, [])  //eslint would complain about workspaces which would result in looping
	/* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    const init = async () => {
      const startingData = await fetchStartingData()

      if (startingData) {
        const hostedModelRes = await fetchHostedModels(supabase, startingData.profile, startingData.tenantId)
        if (!hostedModelRes) {
					logger.warn(`No hosted models, exiting init`)
					return
				}

        setEnvKeyMap(hostedModelRes.envKeyMap)
				setTenantKeyMap(hostedModelRes.tenantKeyMap)
				setTenantBillableKeyMap(hostedModelRes.tenantBillableKeyMap)
				setTenantSharedKeyMap(hostedModelRes.tenantSharedKeyMap)
        setAvailableHostedModels(hostedModelRes.hostedModels)

        if (
          startingData.profile["openrouter_api_key"] ||
          hostedModelRes.envKeyMap["openrouter"] ||
					hostedModelRes.tenantKeyMap["openrouter"] ||
					hostedModelRes.tenantBillableKeyMap["openrouter"] ||
					hostedModelRes.tenantSharedKeyMap["openrouter"]
        ) {
          const openRouterModels = await fetchOpenRouterModels()
          if (!openRouterModels) {
						logger.warn(`No open router models, exiting init`)
						return
					}
          setAvailableOpenRouterModels(openRouterModels)
        }
      }
			else {
				logger.warn(`No starting data received to load models`)
			}

      if (process.env.NEXT_PUBLIC_OLLAMA_URL) {
        const localModels = await fetchOllamaModels()
        if (!localModels) {
					logger.warn(`No local models, exiting init`)
					return
				}
        setAvailableLocalModels(localModels)
      }

      const allUsers = await getAllProfileUsers(supabase)
      setUsers(allUsers)
			setInitialized(true)
    }


		init().catch(error => {
      logError(logger, error, "Error during init")
	  })
  }, [fetchStartingData])

	/**
		Manage the subscription to global content changes
	 */
  useEffect(() => {
		let handlers: HandlerConfig<any>[] = []

    const initSubs = async () => {
			logger.info(`Intializing global subscription handlers`)
			const mgr = getClientSubscriptionManager()
			await mgr.initialize()

			handlers = [
				makeAssistantChangeHandler(setAssistants),
				makeAssistantWorkspaceHandler(workspaceIdRef, setAssistants),
				makeChatHandler(workspaceIdRef, setChats),
				makeCollectionChangeHandler(setCollections),
				makeCollectionWorkspaceHandler(workspaceIdRef, setCollections),
				makeFileChangeHandler(setFiles),
				makeFileWorkspaceHandler(workspaceIdRef, setFiles),
				makeFolderChangeHandler(workspaceIdRef, setFolders),
				makeFolderElementsHandler(workspaceIdRef, setFolders),
				makeGroupChangeHandler(setGroups),
				makeGroupWorkspaceHandler(workspaceIdRef, setGroups),
				makeModelChangeHandler(setModels),
				makeModelWorkspaceHandler(workspaceIdRef, setModels),
				makePresetChangeHandler(setPresets),
				makePresetWorkspaceHandler(workspaceIdRef, setPresets),
				makePromptChangeHandler(setPrompts),
				makePromptWorkspaceHandler(workspaceIdRef, setPrompts),
				makeProviderHandler(setProviders),
				makeMcpChangeHandler(setMcps),
				makeMcpWorkspaceHandler(workspaceIdRef, setMcps),
				makeToolChangeHandler(setTools),
				makeToolWorkspaceHandler(workspaceIdRef, setTools),
				makeWorkspaceHandler(authuserRef, tenantIdRef, setWorkspaces),
			]
			logger.info(`Registering ${handlers.length} handlers for ${workspaceIdRef.current}`)
			await Promise.all(
				handlers.map(handler => mgr.registerHandler(handler))
			)
    }

		if (!userSession) {
			logger.info('[utility.globalState] - userSession not set, skipping global handler registration')
		}
		else {
			initSubs().catch(error => {
				logError(logger, error, "Error during init subscriptions")
			})

			return () => {
				if (handlers.length) {
					logger.info(`Unregistering ${handlers.length} handlers for ${workspaceIdRef.current}`)
					const mgr = getClientSubscriptionManager()
					void (async () => {
						await Promise.all(
							handlers.map(handler => mgr.unregisterHandler(handler.key))
						)
					})()
				}
			}
		}
  }, [userSession])

  return (
    <ChatbotUIContext.Provider
      value={{
			  isInitialized,
				setInitialized,

        // PROFILE STORE
        profile,
        setProfile,

        // USER SETTINGS
        settings,
        setSettings,

        // USER SESSION
        userSession,
        setUserSession,

        // USER SESSIONS
        sessions,
        setSessions,

        // AUTH USER
        authuser,
        setAuthuser,

        // USERS STORE
        users,
        setUsers,

        // ITEMS STORE
        assistants,
        setAssistants,
        collections,
        setCollections,
        chats,
        setChats,
        chatIds,
        setChatIds,
        chatsMap,
				setChatsMap,
        files,
        setFiles,
        folders,
        setFolders,
        mcps,
        setMcps,
        models,
        setModels,
        presets,
        setPresets,
        profiles,
        setProfiles,
        prompts,
        setPrompts,
        providers,
        setProviders,
        tenants,
        setTenants,
        roles,
        setRoles,
        groups,
        setGroups,
        tools,
        setTools,
        workspaces,
        setWorkspaces,

        // MODELS STORE
        envKeyMap,
        setEnvKeyMap,
				tenantKeyMap,
				setTenantKeyMap,
				tenantBillableKeyMap,
				setTenantBillableKeyMap,
				tenantSharedKeyMap,
				setTenantSharedKeyMap,
				allModels,
        availableHostedModels,
        setAvailableHostedModels,
        availableLocalModels,
        setAvailableLocalModels,
        availableOpenRouterModels,
        setAvailableOpenRouterModels,

        // WORKSPACE STORE
        selectedWorkspace,
        setSelectedWorkspace,
        workspaceImages,
        setWorkspaceImages,

        // PRESET STORE
        selectedPreset,
        setSelectedPreset,

        // ASSISTANT STORE
        selectedAssistant,
        setSelectedAssistant,
        assistantImages,
        setAssistantImages,
        openaiAssistants,
        setOpenaiAssistants,

        // PASSIVE CHAT STORE
        chatSettings,
        setChatSettings,
        selectedChat,
        setSelectedChat,
        showDashboard,
        setShowDashboard,

				//REFS
				workspaceIdRef,
			  selectedChatIdRef,
  			authuserRef,
  			tenantIdRef,
				chatMessagesRef,
				setChatMessagesRef,
				setChatFileItemsRef,

				getTenantId,

				validateChats,
				validatePresets,
				validateProfiles,
				validatePrompts,
				validateProviders,
				validateFiles,
				validateCollections,
				validateAssistants,
				validateTenants,
				validateRoles,
				validateGroups,
				validateTools,
				validateMcps,
				validateModels,

				isSharedConfig,
				isProviderKeyActive,
      }}
    >
      {children}
    </ChatbotUIContext.Provider>
  )
}

// GlobalState.whyDidYouRender = true